import { useCallback } from "react";
import { useCurrentAccountId } from "@zapier/identity";
import {
  AUTH_CONNECTION_ERROR,
  AUTH_FAILED_CONN,
  connectToAuth,
  ConnectionErrorPayload,
} from "./connectToAuth";
import { useNotification } from "@zapier/utils";
import { HydratedApp } from "@zapier/client";
import { Implementation } from "@zapier/bff";

const useCreateAppConnection = () => {
  const accountId = useCurrentAccountId();
  const { createNotification } = useNotification();

  const createNewConnection = useCallback(
    async function createNewConnection(
      app: HydratedApp,
      connection?: Partial<{
        destinationSelectedApi: string | null;
        id: string | number | null;
        identifier: string | null;
        isShared: boolean | null;
        isManaged: boolean | null;
        implementation: Implementation | null;
        selectedApi: string | null;
      }>,
    ) {
      const authType =
        app?.implementation?.authType ??
        app?.implementationMeta?.authType ??
        connection?.implementation?.authType;
      // hasUpfrontFields only lives on the implementations-meta payload — the
      // /implementations endpoint does not expose it.
      const hasUpfrontFields =
        app?.implementationMeta?.hasUpfrontFields ?? false;
      // serviceKey resolution chain, most-specific source first:
      //   1. connection.selectedApi      — explicit caller choice (e.g. user
      //      picked an API version in the reconnect/multi-version flow)
      //   2. implementationMeta.id       — ground truth; for private apps this
      //      is the only source that resolves
      //   3. implementation.selectedApi  — primary public-app identifier
      //      (versioned, strip @version)
      //   4. currentImplementationId     — HydratedApp fallback when
      //      `implementation` isn't hydrated yet
      //   5. connection.implementation.* — legacy callers that only pass a
      //      connection without an app
      const serviceKey =
        connection?.selectedApi ||
        app?.implementationMeta?.id ||
        app?.implementation?.selectedApi?.split("@")[0] ||
        app?.currentImplementationId?.split("@")[0] ||
        connection?.implementation?.currentImplementation?.split("@")[0];

      try {
        if (!authType) {
          throw new Error(`${app?.name} does not require connections`);
        }
        if (!serviceKey) {
          throw new Error(
            `Unable to start connection: missing serviceKey for ${app?.name}`,
          );
        }
        // This function is an interface with the engine team to open a popup
        // to a url that handles creating new auth connections to a service
        // defined by the `serviceKey` value passed in. This can be something like
        // TwitterAPI or TwitterAPI@1.0.0
        const id = await connectToAuth({
          accountId,
          authId: connection?.id ? String(connection?.id) : undefined,
          authIdentifier: connection?.identifier || undefined,
          destinationServiceKey:
            connection?.destinationSelectedApi || undefined,
          hasUpfrontFields,
          isOauth: authType === "oauth",
          isShared: connection?.isShared || undefined,
          isManaged: connection?.isManaged || undefined,
          serviceKey,
        });

        // `id` can legitimately be `undefined` here — some OAuth apps never
        // return one (see connectToAuth.ts's `isOauth` handling). `success`
        // is the authoritative signal: it's only `true` when the popup
        // resolved, regardless of whether an `id` came back with it.
        return { app, connection, id, success: true };
      } catch (error) {
        const code = (error as any)?.code;

        // ConnectApp reported a connection failure (e.g. a domain_filter guard
        // rejection). Hand it back to the caller to interpret; don't toast.
        if (code === AUTH_CONNECTION_ERROR) {
          return {
            app,
            connection,
            connectionError: (
              error as { connectionError: ConnectionErrorPayload }
            ).connectionError,
            success: false,
          };
        }

        // AUTH_FAILED_CONN means the user closed the popup — stay silent.
        if (
          code !== AUTH_FAILED_CONN &&
          !(error as any)?.message?.includes("Popup was blocked")
        ) {
          createNotification({
            message: (error as any).toString(),
            type: "error",
          });
        }
      }

      return { app, connection, success: false };
    },
    [accountId, createNotification],
  );

  return createNewConnection;
};

export default useCreateAppConnection;
